import { useEffect, useRef, useState } from "react";
import { captureFrame, getCameraStream, playVideoWithStream, stopCameraStream } from "../utils/cameraUtils";
import { handleFileUpload } from "../utils/fileUtils";
import FileUpload from "./common/FileUpload";
import ModeToggle from "./common/ModeToggle";
import TipsSection from "./common/TipsSection";
import VideoContainer from "./common/VideoContainer";

const NIDCaptureBoth = ({ onCapture, onBack }) => {
  const frontVideoRef = useRef(null);
  const backVideoRef = useRef(null);
  const frontCanvasRef = useRef(null);
  const backCanvasRef = useRef(null);
  const frontFileInputRef = useRef(null);
  const backFileInputRef = useRef(null);
  const [frontStream, setFrontStream] = useState(null);
  const [backStream, setBackStream] = useState(null);
  const [frontCapturedImage, setFrontCapturedImage] = useState(null);
  const [backCapturedImage, setBackCapturedImage] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [flash, setFlash] = useState(false);
  const [mode, setMode] = useState("upload"); // 'camera' or 'upload'

  useEffect(() => {
    if (mode === "camera") {
      startCameras();
    } else {
      stopCameras();
    }
    return () => {
      stopCameras();
    };
  }, [mode]);

  const startCameras = async () => {
    try {
      const frontMediaStream = await getCameraStream({
        facingMode: "environment",
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      });

      const backMediaStream = await getCameraStream({
        facingMode: "environment",
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      });

      setFrontStream(frontMediaStream);
      setBackStream(backMediaStream);

      if (frontVideoRef.current) {
        await playVideoWithStream(frontVideoRef.current, frontMediaStream);
      }

      if (backVideoRef.current) {
        await playVideoWithStream(backVideoRef.current, backMediaStream);
      }
    } catch (error) {
      console.error("Camera error:", error);
      alert(
        "Failed to access camera. Please ensure camera permissions are granted.",
      );
    }
  };

  const stopCameras = () => {
    stopCameraStream(frontStream);
    stopCameraStream(backStream);
    setFrontStream(null);
    setBackStream(null);
  };

  const captureFrontImage = () => {
    if (!frontVideoRef.current) return;

    setIsCapturing(true);
    setFlash(true);

    // Flash effect
    setTimeout(() => {
      setFlash(false);
    }, 100);

    // Capture image
    const imageData = captureFrame(frontVideoRef.current, 0.9);
    setFrontCapturedImage(imageData);

    setTimeout(() => {
      setIsCapturing(false);
    }, 500);
  };

  const captureBackImage = () => {
    if (!backVideoRef.current) return;

    setIsCapturing(true);
    setFlash(true);

    // Flash effect
    setTimeout(() => {
      setFlash(false);
    }, 100);

    // Capture image
    const imageData = captureFrame(backVideoRef.current, 0.9);
    setBackCapturedImage(imageData);

    setTimeout(() => {
      setIsCapturing(false);
    }, 500);
  };

  const handleFrontFileUpload = async (file) => {
    try {
      const imageData = await handleFileUpload(file, { maxSizeInMB: 10 });
      setFrontCapturedImage(imageData);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleBackFileUpload = async (file) => {
    try {
      const imageData = await handleFileUpload(file, { maxSizeInMB: 10 });
      setBackCapturedImage(imageData);
    } catch (error) {
      alert(error.message);
    }
  };

  const retakeImages = () => {
    setFrontCapturedImage(null);
    setBackCapturedImage(null);
  };

  const handleProceed = () => {
    if (!frontCapturedImage || !backCapturedImage) {
      alert("Please capture both NID front and back images");
      return;
    }
    onCapture({
      front: frontCapturedImage,
      back: backCapturedImage,
    });
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-4xl">
        <div className="card">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Upload NID Images
            </h2>
            <p className="text-gray-600">
              Upload both front and back images of your NID card
            </p>
          </div>

          {/* Mode Toggle */}
          <ModeToggle mode={mode} onModeChange={setMode} />

          {/* NID Images Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Front NID */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center">
                NID Front
              </h3>

              {!frontCapturedImage ? (
                <>
                  <VideoContainer
                    videoRef={frontVideoRef}
                    canvasRef={frontCanvasRef}
                    mode={mode}
                    flash={flash}
                    overlayText="Align NID front within the frame"
                    uploadText="Upload NID front image"
                    capturedText="✓ Front Captured"
                  />

                  <FileUpload
                    fileInputRef={frontFileInputRef}
                    onFileChange={handleFrontFileUpload}
                    onCapture={captureFrontImage}
                    mode={mode}
                    isCapturing={isCapturing}
                    buttonText={mode === "camera" ? "Capture" : "Choose File"}
                  />
                </>
              ) : (
                <VideoContainer
                  mode="captured"
                  capturedImage={frontCapturedImage}
                  capturedText="✓ Front Captured"
                />
              )}
            </div>

            {/* Back NID */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center">
                NID Back
              </h3>

              {!backCapturedImage ? (
                <>
                  <VideoContainer
                    videoRef={backVideoRef}
                    canvasRef={backCanvasRef}
                    mode={mode}
                    flash={flash}
                    overlayText="Align NID back within the frame"
                    uploadText="Upload NID back image"
                    capturedText="✓ Back Captured"
                  />

                  <FileUpload
                    fileInputRef={backFileInputRef}
                    onFileChange={handleBackFileUpload}
                    onCapture={captureBackImage}
                    mode={mode}
                    isCapturing={isCapturing}
                    buttonText={mode === "camera" ? "Capture" : "Choose File"}
                  />
                </>
              ) : (
                <VideoContainer
                  mode="captured"
                  capturedImage={backCapturedImage}
                  capturedText="✓ Back Captured"
                />
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center mt-6">
            <button onClick={retakeImages} className="btn-secondary flex-1">
              Retake Images
            </button>
            <button
              onClick={handleProceed}
              disabled={!frontCapturedImage || !backCapturedImage}
              className="btn-primary flex-1"
            >
              Process with OCR
            </button>
          </div>

          {/* Back Button */}
          <div className="text-center mt-4">
            <button onClick={onBack} className="btn-secondary">
              Back
            </button>
          </div>

          {/* Tips */}
          <TipsSection
            tips={[
              'Ensure good lighting',
              'Hold the NID card steady',
              'Keep the NID card flat and within the frame',
              'Avoid glare or reflections',
              'Make sure all text is clearly visible',
              'Use upload for existing photos or camera for live capture'
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default NIDCaptureBoth;
